import { AlertCircle, Inbox, RefreshCw } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import SuggestionCard from "../../components/suggestions/SuggestionCard";
import { useSuggestions } from "../../context/SuggestionsContext";
import { NotificationFeedback } from "./notifications/notificationsUi";

const NotificationsSkeleton = () => (
    <div className="flex flex-col gap-3">
        {[0, 1, 2].map(i => (
            <Card key={i}>
                <CardContent className="flex flex-col gap-3 p-4">
                    <div className="flex items-center gap-3">
                        <Skeleton className="h-10 w-10 rounded-full" />
                        <div className="flex-1 space-y-2">
                            <Skeleton className="h-4 w-40" />
                            <Skeleton className="h-3 w-24" />
                        </div>
                    </div>
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-8 w-24" />
                </CardContent>
            </Card>
        ))}
    </div>
);

/**
 * Embedded mobile view of the user's notifications (aka "suggestions"): the same
 * items the header bell / home page show, rendered as a scrollable single-column
 * card list. Each card is interactive inline (complete actions, open the submit
 * form, dismiss) via the shared SuggestionCard. Host links to a single item with
 * #/embedded-app/notifications/detail?id=<Id>.
 */
const Notifications = () => {
    const { suggestions, suggestionsLoading, suggestionError, refreshSuggestions } = useSuggestions();

    const count = suggestions.length;

    return (
        <div className="mx-auto flex w-full max-w-xl flex-col gap-3 p-3 pb-8">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-lg font-semibold">Notifications</h1>
                    <p className="text-xs text-muted-foreground">
                        {suggestionsLoading && count === 0
                            ? 'Loading your notifications...'
                            : `${count} notification${count !== 1 ? 's' : ''}`}
                    </p>
                </div>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => refreshSuggestions()}
                    disabled={suggestionsLoading}
                    aria-label="Refresh notifications"
                >
                    <RefreshCw className={suggestionsLoading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
                </Button>
            </div>

            {suggestionError && (
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Couldn't load notifications</AlertTitle>
                    <AlertDescription>{suggestionError}</AlertDescription>
                </Alert>
            )}

            {suggestionsLoading && count === 0 && <NotificationsSkeleton />}

            {!suggestionsLoading && !suggestionError && count === 0 && (
                <div className="flex flex-col items-center gap-2 py-12 text-center text-muted-foreground">
                    <Inbox className="h-8 w-8" />
                    <p className="text-sm">You're all caught up. No notifications right now.</p>
                </div>
            )}

            {suggestions.map(suggestion => (
                <SuggestionCard
                    key={suggestion.Id}
                    suggestion={suggestion}
                    size="full"
                    refreshParent={refreshSuggestions}
                    showDocuSignMessage={true}
                    adminMode={false}
                />
            ))}

            <NotificationFeedback />
        </div>
    );
};

export default Notifications;
